import React, { useState, useRef, useEffect, useId } from 'react';
import classNames from 'classnames';
import { Button } from '../Button';
import { Icon } from '../Icon';
import styles from './ToolTip.module.scss';

interface ToolTipProps {
	className?: string;
	text: string;
	/**
	 * Accessible name for the trigger. Must identify what the tooltip describes,
	 * e.g. "More information about Oil volume" - a generic "Info" repeated down the
	 * page leaves screen reader users unable to tell the triggers apart (WCAG 2.4.6).
	 */
	label: string;
	/** Widens the panel for long richtext, e.g. PIM attribute descriptions */
	wider?: boolean;
}

type Side = 'bottom' | 'top';

// Matches $arrowSize in ToolTip.module.scss
const arrowSize = 12;

export const ToolTip: React.FunctionComponent<ToolTipProps> = ({ className, text, label, wider }) => {
	const [showText, setShowText] = useState(false);
	const [side, setSide] = useState<Side>('bottom');
	const [arrowX, setArrowX] = useState<number | null>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const textId = useId();

	const showTooltip = () => {
		setShowText(true);
	};

	const hideTooltip = () => {
		setShowText(false);
		// Clear it, so a reopen never paints a frame with the previous trigger's offset
		setArrowX(null);
	};

	// Touch logic: only show on touchend, hide on touchstart if already shown
	const handleTouchEnd = () => {
		if (!showText) {
			showTooltip();
		}
	};
	const handleTouchStart = () => {
		if (showText) {
			hideTooltip();
		}
	};

	// CSS places the panel but exposes no selector for the fallback it landed on, so read the
	// result back and point the arrow at the trigger
	useEffect(() => {
		const panelElement = panelRef.current;

		if (!showText || !panelElement || !buttonRef.current) {
			return;
		}

		const panel = panelElement.getBoundingClientRect();
		const trigger = buttonRef.current.getBoundingClientRect();

		// Centres, not edges. The panel sits flush against the trigger, so its top edge and the
		// trigger's bottom edge are equal by design - comparing those two let sub-pixel rounding
		// from display scaling decide the side, which drew the arrow above the icon.
		setSide(panel.top + panel.height / 2 >= trigger.top + trigger.height / 2 ? 'bottom' : 'top');

		// Insets run from the padding box, so clientLeft takes the border off. Clamped to keep
		// the arrow on the panel where the trigger sits outside it - the stretched phone panel,
		// or an inline fallback.
		const offset = trigger.left + trigger.width / 2 - panel.left - panelElement.clientLeft - arrowSize / 2;

		setArrowX(Math.min(Math.max(offset, 0), panelElement.clientWidth - arrowSize));
	}, [showText]);

	// position-try re-evaluates as the page scrolls and can flip the panel to the other side of
	// the trigger. The side is measured once on open, so close rather than let the arrow point
	// the wrong way.
	useEffect(() => {
		if (!showText) {
			return;
		}

		const handleScroll = (event: Event) => {
			// Capture catches scrolling containers too, but scrolling the panel's own richtext
			// is the one scroll that must not dismiss it
			if (!panelRef.current?.contains(event.target as Node)) {
				hideTooltip();
			}
		};

		document.addEventListener('scroll', handleScroll, { capture: true, passive: true });

		return () => document.removeEventListener('scroll', handleScroll, { capture: true });
	}, [showText]);

	// WCAG 1.4.13: dismissible without moving pointer or focus. Tabbing away does neither.
	useEffect(() => {
		if (!showText) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				hideTooltip();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [showText]);

	return (
		// Hover on the container, not the button, so the pointer can reach the panel to scroll it
		<div
			className={classNames(styles.toolTipContainer, className)}
			onMouseEnter={showTooltip}
			onMouseLeave={hideTooltip}
		>
			<Button
				className={styles.trigger}
				aria-label={label}
				aria-describedby={showText ? textId : undefined}
				hasOnlyIcon
				variant="ghost"
				ref={buttonRef}
				onFocus={showTooltip}
				onBlur={hideTooltip}
				onTouchEnd={handleTouchEnd}
				onTouchStart={handleTouchStart}
			>
				<Icon name="info" size="md" className={styles.icon} />
			</Button>
			{showText && (
				<div
					id={textId}
					ref={panelRef}
					role="tooltip"
					className={classNames(styles.textWrapper, styles[side], {
						[styles.wider]: wider,
					})}
					style={
						arrowX === null ? undefined : ({ '--cc-tooltip-arrow-x': `${arrowX}px` } as React.CSSProperties)
					}
				>
					<div className={styles.content} dangerouslySetInnerHTML={{ __html: text }} />
				</div>
			)}
		</div>
	);
};
